/**
 * MonthPlan — plan any month ahead of the one I am in (#392, #393, #394).
 *
 * Per bank I show the opening balance carried from last month (its own
 * Carried column, with a "carried from <month>" hint under the title, #624),
 * what comes in (carried plus planned income and money moved in from my other
 * banks), what goes out, and what is left, with a total row. Under it a Simulate list lets me tick incomes and expenses "as if"
 * they happened and shows what would then be in each bank. Ticks are never
 * saved: the numbers come from GET /budget/months/<id>/plan/ and the maths
 * from plan.js, the same rules the phone uses.
 *
 * Once the month's payday hand-off is confirmed (transferred and started, the
 * server's `handed_over`), the card gets a Close button. Closing is remembered
 * per month in this browser and leaves a one-line "Show plan" link to reopen.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
import { fmtR } from '../money.js';
import { computePlan, initialTicks, plannedRows } from '../plan.js';

/**
 * @param {{ budgetId: number, monthLabel: string, refreshKey?: any }} props
 * refreshKey — anything that changes when the month's items change, so the
 * plan re-reads after an edit elsewhere on the dashboard.
 */
export default function MonthPlan({ budgetId, monthLabel, refreshKey }) {
  const [plan, setPlan] = useState(null);
  const [ticked, setTicked] = useState(() => new Set());
  const [open, setOpen] = useState(false);
  const closedKey = `vault_plan_closed_${budgetId}`;
  const [closed, setClosed] = useState(() => {
    try { return localStorage.getItem(closedKey) === '1'; } catch { return false; }
  });
  // A different month on screen reads its own remembered choice.
  useEffect(() => {
    try { setClosed(localStorage.getItem(closedKey) === '1'); } catch { setClosed(false); }
  }, [closedKey]);
  /** Close or reopen the card and remember it for this month in this browser. */
  const setClosedFor = (v) => {
    setClosed(v);
    try { if (v) localStorage.setItem(closedKey, '1'); else localStorage.removeItem(closedKey); } catch { /* storage blocked: close for this visit only */ }
  };

  useEffect(() => {
    let live = true;
    if (!budgetId) return undefined;
    api(`/budget/months/${budgetId}/plan/`).then((p) => {
      if (!live) return;
      setPlan(p);
      setTicked(initialTicks(p));
    }).catch(() => {});
    return () => { live = false; };
  }, [budgetId, refreshKey]);

  const table = useMemo(() => plannedRows(plan), [plan]);
  const sim = useMemo(() => computePlan(plan, ticked), [plan, ticked]);
  if (!plan) return null;
  // Only a month that is handed over and started may be closed.
  if (plan.handed_over && closed) {
    return (
      <div className="mb-5 text-sm text-slate-500">
        Plan {monthLabel} closed ·{' '}
        <button className="text-vblue hover:underline" onClick={() => setClosedFor(false)}>Show plan</button>
      </div>
    );
  }

  const toggle = (id) => setTicked((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const bankName = (id) => (plan.banks.find((b) => b.bank === id) || {}).bank_name || 'No bank';
  const lines = (kind) => plan.items.filter((i) => i.kind === kind && Number(i.amount) !== 0);
  // Where the opening balances came from (#624): the month before this one.
  const prevLabel = plan.prev_month
    ? new Date(`${plan.prev_month}-01T00:00:00`).toLocaleDateString('en-ZA', { month: 'long' })
    : null;

  return (
    <section className="card mb-5" data-guide="month-plan">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-bold text-slate-100">Plan {monthLabel}</h2>
        <span className="flex items-center gap-3">
          <span className="text-[11px] text-slate-500">nothing here is saved</span>
          {plan.handed_over && (
            <button className="text-slate-400 hover:text-white text-sm" onClick={() => setClosedFor(true)}
              aria-label={`Close plan ${monthLabel}`} title="Month started — close the plan">✕ Close</button>
          )}
        </span>
      </div>
      {prevLabel && (
        <p className="text-[11px] text-slate-500 mt-0.5" data-guide="month-plan-carried">
          Opening balances carried from {prevLabel}
        </p>
      )}
      <table className="w-full text-sm mt-3" data-guide="month-plan-table">
        <thead>
          <tr className="text-[11px] uppercase tracking-wider text-slate-500">
            <th className="text-left font-medium py-1">Bank</th>
            <th className="text-right font-medium" title="Opening balance brought forward from last month (already inside In)">Carried</th>
            <th className="text-right font-medium">In</th>
            <th className="text-right font-medium">Out</th>
            <th className="text-right font-medium">Left</th>
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r) => (
            <tr key={r.bank} className="border-t border-edge/50">
              <td className="py-1.5 text-slate-200">{r.bank_name}</td>
              <td className="text-right text-slate-400">{fmtR(r.carried)}</td>
              <td className="text-right text-emerald-400">{fmtR(r.moneyIn)}</td>
              <td className="text-right text-red-400">{fmtR(r.plannedOut)}</td>
              <td className={`text-right font-semibold ${r.leftover < 0 ? 'text-red-400' : 'text-slate-100'}`}>{fmtR(r.leftover)}</td>
            </tr>
          ))}
          <tr className="border-t border-edge">
            <td className="py-1.5 font-semibold text-slate-100">Total</td>
            <td className="text-right text-slate-400">{fmtR(table.total.carried)}</td>
            <td className="text-right text-emerald-400">{fmtR(table.total.moneyIn)}</td>
            <td className="text-right text-red-400">{fmtR(table.total.plannedOut)}</td>
            <td className={`text-right font-bold ${table.total.leftover < 0 ? 'text-red-400' : 'text-slate-100'}`}>{fmtR(table.total.leftover)}</td>
          </tr>
        </tbody>
      </table>

      <button className="mt-3 text-sm text-vblue hover:underline" data-guide="month-plan-simulate" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide simulation' : 'Simulate: tick what gets paid or received'}
      </button>
      {open && (
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          {['income', 'expense'].map((kind) => (
            <div key={kind}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1.5">
                {kind === 'income' ? 'Money in — ticked means received' : 'Money out — ticked means paid'}
              </div>
              <div className="flex flex-col gap-1">
                {lines(kind).map((i) => (
                  <label key={i.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={ticked.has(i.id)} onChange={() => toggle(i.id)} />
                    <span className="flex-1 min-w-0 truncate text-slate-200">{i.title}</span>
                    <span className="text-[11px] text-slate-500 shrink-0">
                      {i.from_bank ? `${bankName(i.from_bank)} → ${bankName(i.bank)}` : bankName(i.bank)}
                    </span>
                    <span className="shrink-0 w-24 text-right">{fmtR(i.amount)}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="md:col-span-2 border-t border-edge/60 pt-2" data-guide="month-plan-sim-result">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">In the bank if these happen</div>
            {sim.banks.map((b) => (
              <div key={String(b.bank)} className="flex justify-between text-sm">
                <span className="text-slate-300">{b.bank_name}</span>
                <span className={b.simulated < 0 ? 'text-red-400' : 'text-slate-100'}>{fmtR(b.simulated)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-semibold mt-1">
              <span>Total</span><span>{fmtR(sim.total)}</span>
            </div>
            <div className="flex gap-3 mt-2 text-xs">
              <button className="text-slate-400 hover:text-slate-200" onClick={() => setTicked(new Set(plan.items.map((i) => i.id)))}>Tick all</button>
              <button className="text-slate-400 hover:text-slate-200" onClick={() => setTicked(initialTicks(plan))}>Reset</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
